import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';
import { useToast } from '../components/Toast';
import { openPdf } from '../utils/pdf';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

type Tpl = {
  key: string; soort: string; onderdeel: string; titel: string; waar_gebruikt: string;
  variabelen: string[]; kanaal?: string; portal?: string; overschreven: boolean;
  onderwerp?: string; inhoud?: string; css?: string; beschikbaar?: boolean;
  standaard_onderwerp?: string; standaard_inhoud?: string; standaard_css?: string;
};

const SOORTEN: { key: string; label: string }[] = [
  { key: 'bericht', label: 'Berichten (portaal & e-mail)' },
  { key: 'document', label: 'PDF-documenten' },
  { key: 'portaltekst', label: 'Portalteksten' },
];

export const AdminTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };
  const notify = useToast();

  const [items, setItems] = useState<Tpl[]>([]);
  const [loading, setLoading] = useState(true);
  const [soort, setSoort] = useState('bericht');
  const [edit, setEdit] = useState<Tpl | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/admin/templates`, { headers })
      .then((r) => (r.ok ? r.json() : [])).then(setItems).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    load();
  }, [isAuthenticated, accessToken, navigate, load]);

  const getroepeerd = () => {
    const sel = items.filter((t) => t.soort === soort);
    const groepen: Record<string, Tpl[]> = {};
    sel.forEach((t) => { (groepen[t.onderdeel] = groepen[t.onderdeel] || []).push(t); });
    return groepen;
  };

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="font-display text-2xl font-semibold text-mfb-blue">Templates</h1>
        <p className="text-sm text-slate-500 max-w-3xl">
          Alle standaardberichten, PDF-documenten en portalteksten centraal beheren. Per template zie je <strong>waar het gebruikt wordt</strong> en
          welke <strong>variabelen</strong> beschikbaar zijn. Bewerk een tekst en hij wordt direct gebruikt; met “Herstellen” keer je terug naar de standaard.
        </p>
      </div>

      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {SOORTEN.map((s) => (
          <button key={s.key} onClick={() => setSoort(s.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border ${soort === s.key ? 'bg-mfb-blue text-white border-mfb-blue' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {soort === 'document' && (
        <div className="mb-4 rounded-xl border border-mfb-blue/20 bg-mfb-blue/5 p-3 text-sm text-slate-600">
          PDF-documenten zijn volledig zelf op te maken met HTML + CSS. Gebruik <code className="text-mfb-blue">{'{{ variabele }}'}</code> voor gegevens en bekijk een voorbeeld-PDF terwijl je bewerkt.
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-500">Laden…</div>
      ) : (
        <div className="space-y-8">
          {Object.entries(getroepeerd()).map(([onderdeel, tpls]) => (
            <div key={onderdeel}>
              <h2 className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-3">{onderdeel}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tpls.map((t) => (
                  <button key={t.key} onClick={() => setEdit(t)}
                    className="text-left bg-white rounded-2xl border border-slate-200 p-4 hover:shadow-md hover:-translate-y-0.5 transition">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-mfb-blue">{t.titel}</div>
                      {t.overschreven
                        ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-mfb-orange/10 text-mfb-orange font-medium whitespace-nowrap">aangepast</span>
                        : <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 whitespace-nowrap">standaard</span>}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">📍 {t.waar_gebruikt}</div>
                    {t.soort === 'bericht' && t.onderwerp && <div className="text-xs text-slate-600 mt-2 line-clamp-2"><span className="text-slate-400">Onderwerp:</span> {t.onderwerp}</div>}
                    {(t.kanaal || t.portal) && <div className="text-[10px] text-slate-400 mt-2">{t.kanaal ? `Kanaal: ${t.kanaal}` : `Portal: ${t.portal}`}</div>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {edit && (
        <TemplateEditor tpl={edit} token={accessToken!} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} notify={notify} />
      )}
    </Layout>
  );
};

const TemplateEditor: React.FC<{ tpl: Tpl; token: string; onClose: () => void; onSaved: () => void; notify: any }> = ({ tpl, token, onClose, onSaved, notify }) => {
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const isBericht = tpl.soort === 'bericht';
  const isDoc = tpl.soort === 'document';
  const [onderwerp, setOnderwerp] = useState(tpl.onderwerp || '');
  const [inhoud, setInhoud] = useState(tpl.inhoud || '');
  const [css, setCss] = useState(tpl.css || '');
  const [preview, setPreview] = useState<{ onderwerp?: string; body?: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const opslaan = async () => {
    setBusy(true);
    try {
      const body: any = { inhoud };
      if (isBericht) body.onderwerp = onderwerp;
      if (isDoc) body.css = css;
      const r = await fetch(`${API_BASE_URL}/api/admin/templates/${tpl.key}`, { method: 'PUT', headers, body: JSON.stringify(body) });
      if (!r.ok) throw new Error();
      notify.success('Template opgeslagen — wordt direct gebruikt.');
      onSaved();
    } catch { notify.error('Opslaan mislukt'); } finally { setBusy(false); }
  };
  const herstellen = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/templates/${tpl.key}`, { method: 'DELETE', headers });
      if (!r.ok) throw new Error();
      notify.success('Standaardtekst hersteld.');
      onSaved();
    } catch { notify.error('Herstellen mislukt'); } finally { setBusy(false); }
  };
  const toonPreview = async () => {
    const r = await fetch(`${API_BASE_URL}/api/admin/templates/${tpl.key}/preview`, { method: 'POST', headers, body: JSON.stringify({}) });
    if (r.ok) setPreview(await r.json());
  };

  const insertVar = (v: string) => setInhoud((s) => `${s}{${v}}`);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <h2 className="text-lg font-semibold text-mfb-blue">{tpl.titel}</h2>
            <p className="text-xs text-slate-400">📍 {tpl.waar_gebruikt} · <code className="text-slate-400">{tpl.key}</code></p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>

        {isDoc && tpl.beschikbaar === false ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Dit document wordt nog door de vaste lay-out gegenereerd en is nog niet als HTML-sjabloon beschikbaar. De vijf klant-brieven (verzekeringsbewijs, afwijzing verzekering/schade, schadestaat, incasso-afrekening) zijn dat wel.
          </div>
        ) : isDoc ? (
          <div className="mt-4 space-y-4">
            <div className="rounded-lg border border-mfb-blue/20 bg-mfb-blue/5 p-3 text-xs text-slate-600">
              Opmaak met HTML + CSS. Gebruik <code className="text-mfb-blue">{'{{ variabele }}'}</code> voor gegevens (bijv. <code className="text-mfb-blue">{'{{ factuur }}'}</code>) en <code className="text-mfb-blue">{'{{ org.handelsnaam }}'}</code> voor afzendergegevens. De header (logo + afzender) en footer (verzekeraar) worden automatisch toegevoegd.
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">HTML-sjabloon</label>
              <textarea value={inhoud} onChange={(e) => setInhoud(e.target.value)} rows={12}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs font-mono" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">CSS</label>
              <textarea value={css} onChange={(e) => setCss(e.target.value)} rows={8}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs font-mono" />
            </div>
            <div className="flex items-center justify-between gap-2 pt-2">
              <div className="flex gap-2">
                <button onClick={() => openPdf(`/api/admin/templates/${tpl.key}/document-preview`, token)}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">📄 Voorbeeld-PDF</button>
                {tpl.overschreven && <button onClick={herstellen} disabled={busy} className="rounded-lg px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">Herstel standaard</button>}
              </div>
              <div className="flex gap-2">
                <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Annuleren</button>
                <button onClick={opslaan} disabled={busy} className="rounded-lg bg-mfb-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50">{busy ? 'Opslaan…' : 'Opslaan'}</button>
              </div>
            </div>
            <p className="text-[11px] text-slate-400">Tip: sla eerst op en bekijk daarna de voorbeeld-PDF om je wijziging te zien.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {tpl.variabelen?.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Beschikbare variabelen (klik om in te voegen)</div>
                <div className="flex flex-wrap gap-1.5">
                  {tpl.variabelen.map((v) => (
                    <button key={v} onClick={() => insertVar(v)} className="px-2 py-0.5 rounded-md bg-mfb-blue/5 text-mfb-blue text-xs font-mono border border-mfb-blue/20 hover:bg-mfb-blue/10">{`{${v}}`}</button>
                  ))}
                </div>
              </div>
            )}

            {isBericht && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Onderwerp</label>
                <input value={onderwerp} onChange={(e) => setOnderwerp(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{isBericht ? 'Berichttekst' : 'Tekst'}</label>
              <textarea value={inhoud} onChange={(e) => setInhoud(e.target.value)} rows={isBericht ? 8 : 5}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" />
            </div>

            {preview && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Voorbeeld (met [variabele] als placeholder)</div>
                {preview.onderwerp && <div className="text-sm font-semibold text-mfb-blue">{preview.onderwerp}</div>}
                <div className="mt-1 text-sm text-slate-700 whitespace-pre-line">{preview.body}</div>
              </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-2">
              <div className="flex gap-2">
                <button onClick={toonPreview} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Voorbeeld</button>
                {tpl.overschreven && <button onClick={herstellen} disabled={busy} className="rounded-lg px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">Herstel standaard</button>}
              </div>
              <div className="flex gap-2">
                <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Annuleren</button>
                <button onClick={opslaan} disabled={busy} className="rounded-lg bg-mfb-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50">{busy ? 'Opslaan…' : 'Opslaan'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTemplatesPage;
